@file:Suppress(
    "RETURN_VALUE_NOT_USED",
    "RETURN_VALUE_NOT_USED_COERCION",
)

import com.varabyte.kobweb.browser.api
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Suppress
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.dom.hasClass
import kotlinx.dom.removeClass
import org.jetbrains.compose.web.renderComposable
import org.w3c.dom.EventSource
import org.w3c.dom.EventSourceInit
import org.w3c.dom.MessageEvent
import org.w3c.dom.`get`
import com.royceecoleman.bill.pages.accountManagementPage as _com_royceecoleman_bill_pages_accountManagementPage
import com.royceecoleman.bill.pages.aiChatPage as _com_royceecoleman_bill_pages_aiChatPage
import com.royceecoleman.bill.pages.analyticsPage as _com_royceecoleman_bill_pages_analyticsPage
import com.royceecoleman.bill.pages.createAccountPage as _com_royceecoleman_bill_pages_createAccountPage
import com.royceecoleman.bill.pages.dashboardPage as _com_royceecoleman_bill_pages_dashboardPage
import com.royceecoleman.bill.pages.homePage as _com_royceecoleman_bill_pages_homePage
import com.royceecoleman.bill.pages.loginPage as _com_royceecoleman_bill_pages_loginPage
import com.royceecoleman.bill.pages.settingsPage as _com_royceecoleman_bill_pages_settingsPage

private fun forceReloadNow() {
    window.stop()
    window.location.reload()
}

private fun handleServerStatusEvents() {
    val status = document.getElementById("status")!!
    var lastVersion: Int? = null
    var shouldReload = false

    val warningIcon = status.children[0]!!
    val spinnerIcon = status.children[1]!!
    val statusText = status.children[2]!!

    status.addEventListener("transitionend", {
        if (status.hasClass("fade-out")) {
            status.removeClass("fade-out")
            if (shouldReload) {
                forceReloadNow()
            }
        }
    })

    val eventSource = EventSource("/api/kobweb-status", EventSourceInit(true))
    eventSource.addEventListener("version", { evt ->
        val version = (evt as MessageEvent).data.toString().toInt()
        if (lastVersion == null) {
            lastVersion = version
        }
        if (lastVersion != version) {
            lastVersion = version
            if (document.asDynamic().hidden) {
                // Reload immediately when the page is not visible as the animation will not run
                forceReloadNow()
            } else if (status.className.isNotEmpty()) {
                shouldReload = true
            } else {
                // Not sure if we can get here but if we can't rely on the status transition
                // to reload we should do it ourselves.
                forceReloadNow()
            }
        }
    })

    eventSource.addEventListener("status", { evt ->
        val values: dynamic = JSON.parse<Any>((evt as MessageEvent).data.toString())
        val text = values.text as String
        val isError = (values.isError as String).toBoolean()
        if (text.isNotBlank()) {
            warningIcon.className = if (isError) "visible" else "hidden"
            spinnerIcon.className = if (isError) "hidden" else "visible"
            statusText.innerHTML = "<i>$text</i>"
            status.className = "fade-in"
        } else {
            if (status.className == "fade-in") {
                status.className = "fade-out"
            }
        }
    })

    eventSource.onerror = { eventSource.close() }
}

public fun main() {
    handleServerStatusEvents()

    window.api.logOnError = true

    AppGlobals.initialize(mapOf("title" to "Bill"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.register(
            "/",
        ) { pageCtx -> 
            _com_royceecoleman_bill_pages_homePage()
        }
        ctx.router.register(
            "/account-management",
        ) { pageCtx -> 
            _com_royceecoleman_bill_pages_accountManagementPage()
        }
        ctx.router.register(
            "/ai-chat",
        ) { pageCtx -> 
            _com_royceecoleman_bill_pages_aiChatPage()
        }
        ctx.router.register(
            "/analytics",
        ) { pageCtx -> 
            _com_royceecoleman_bill_pages_analyticsPage()
        }
        ctx.router.register(
            "/create-account",
        ) { pageCtx -> 
            _com_royceecoleman_bill_pages_createAccountPage()
        }
        ctx.router.register(
            "/dashboard",
        ) { pageCtx -> 
            _com_royceecoleman_bill_pages_dashboardPage()
        }
        ctx.router.register(
            "/login",
        ) { pageCtx -> 
            _com_royceecoleman_bill_pages_loginPage()
        }
        ctx.router.register(
            "/settings",
        ) { pageCtx -> 
            _com_royceecoleman_bill_pages_settingsPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        com.varabyte.kobweb.silk.SilkApp {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
